
import { useEffect, useState } from "react";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Cell
} from "recharts";
import { useIsMobile } from "@/hooks/use-mobile";

interface Metric {
  title: string;
  score: number;
  status: "good" | "warning" | "bad";
  suggestion?: string;
}

interface SeoMetricsChartProps {
  metrics: Metric[];
}

export const SeoMetricsChart = ({ metrics }: SeoMetricsChartProps) => {
  const [chartData, setChartData] = useState<any[]>([]);
  const [animated, setAnimated] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    // Start with empty data (for animation)
    const initialData = metrics.map(metric => ({
      name: metric.title,
      score: 0,
      fullScore: metric.score,
      status: metric.status
    }));
    
    setChartData(initialData);
    
    // Animate the data over time
    const timer = setTimeout(() => {
      const animatedData = metrics.map(metric => ({
        name: metric.title,
        score: metric.score,
        fullScore: metric.score,
        status: metric.status
      }));
      
      setChartData(animatedData);
      setAnimated(true);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [metrics]);

  const getBarColor = (status: string) => {
    switch (status) {
      case "good":
        return "#5F9E6F"; // desert-cactus
      case "warning":
        return "#FF7F50"; // desert-sunset
      case "bad":
        return "#C25B3F"; // desert-canyon
      default:
        return "#87CEEB"; // desert-sky
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-background p-3 rounded-md shadow-lg border border-border">
          <p className="font-medium">{data.name}</p>
          <p className="text-sm">
            Score: <span className="font-medium">{data.score}/100</span>
          </p>
          <p className="text-sm capitalize">
            Status: <span className="font-medium">{data.status}</span>
          </p>
          {data.suggestion && (
            <p className="text-xs mt-1 text-muted-foreground">{data.suggestion}</p>
          )}
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="w-full h-64 md:h-72">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          layout={isMobile ? "vertical" : "horizontal"}
          margin={{ 
            top: 5, 
            right: 5, 
            bottom: isMobile ? 5 : 40, 
            left: isMobile ? 100 : 20 
          }}
        >
          <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
          {isMobile ? (
            <>
              <XAxis type="number" domain={[0, 100]} />
              <YAxis 
                type="category" 
                dataKey="name" 
                tick={{ fontSize: 11 }}
                width={95}
                tickFormatter={(value) => value.length > 12 ? `${value.substring(0, 12)}...` : value}
              />
            </>
          ) : (
            <>
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis type="number" domain={[0, 100]} />
            </>
          )}
          <Tooltip content={<CustomTooltip />} />
          <Bar
            dataKey="score"
            animationDuration={2000}
            animationEasing="ease-out"
          >
            {chartData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={getBarColor(entry.status)} 
                fillOpacity={animated ? 1 : 0.7}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SeoMetricsChart;
